
<template>
  <section
    class="order-contracts-panel"
    :class="{ mobile: variant === 'mobile' }"
    aria-live="polite"
  >
    <header>
      <div>
        <span>{{ variant === 'mobile' ? 'Contracts · 城市合伙人' : '城市合伙人合同' }}</span>
        <strong>{{ contract.statusText || '合同待生成' }}</strong>
      </div>
      <span class="contract-status" :class="statusClass">{{ contract.statusText || '待支付' }}</span>
    </header>

    <p class="contract-effect-tip">
      城市合伙人权益在订单/支付审核通过后即时生效；合同签署进度仅用于跟踪纸质合同归档，不影响权益生效。纸质合同以公司邮寄为主，在线预览与下载用于快速确认和兜底。
    </p>

    <div v-if="loading" class="contract-state">正在加载合同信息...</div>
    <div v-else-if="message" class="contract-state error" role="alert">
      <p>{{ message }}</p>
      <button type="button" @click="loadContracts">重新加载</button>
    </div>
    <template v-else>
      <div v-if="contract.enterpriseName || contract.mailingAddress" class="contract-info">
        <div v-if="contract.enterpriseName">
          <span>签署企业</span>
          <strong>{{ contract.enterpriseName }}</strong>
        </div>
        <div v-if="contract.mailingAddress">
          <span>合同邮寄至</span>
          <strong>{{ contract.mailingAddress }}</strong>
        </div>
      </div>

      <div class="contract-list-head">
        <span>合同清单</span>
        <span>{{ contract.records.length }} 份</span>
      </div>
      <p v-if="!contract.records.length" class="contract-empty">
        支付成功并匹配合同模板后将生成合同；如长时间未生成，请联系客服处理。
      </p>
      <article v-for="record in contract.records" :key="record.id" class="contract-record">
        <div>
          <strong>{{ record.templateName || `城市合伙人合同 ${record.sequence}` }}</strong>
          <span>
            {{ record.contractNo || '合同编号待生成' }}
            <template v-if="record.templateVersion"> · v{{ record.templateVersion }}</template>
          </span>
          <em :class="`record-${record.status.toLowerCase()}`">{{ record.statusText }}</em>
        </div>
        <div class="record-actions">
          <button
            type="button"
            class="preview"
            :disabled="!record.downloadEnabled || previewLoading"
            @click="openContractPreview(record)"
          >
            在线预览
          </button>
          <button
            type="button"
            :disabled="!record.downloadEnabled || downloadingId === record.id"
            @click="downloadContract(record)"
          >
            {{ downloadingId === record.id ? '下载中...' : '下载合同' }}
          </button>
        </div>
      </article>

      <div class="contract-courier">
        <h3>公司寄出快递</h3>
        <dl>
          <div>
            <dt>快递公司</dt>
            <dd>{{ contract.outboundCourierCompany || '待录入' }}</dd>
          </div>
          <div>
            <dt>快递单号</dt>
            <dd>{{ contract.outboundCourierNo || '待录入' }}</dd>
          </div>
          <div v-if="contract.outboundTime">
            <dt>寄出时间</dt>
            <dd>{{ formatDateTime(contract.outboundTime) }}</dd>
          </div>
        </dl>
      </div>

      <div class="contract-courier return-form">
        <h3>寄回公司与物流</h3>
        <dl>
          <div>
            <dt>收件人</dt>
            <dd>
              <button
                type="button"
                class="copy-field"
                :disabled="!contract.returnAddress.recipient"
                :aria-label="`复制收件人 ${contract.returnAddress.recipient || ''}`"
                @click="copyReturnField('recipient', contract.returnAddress.recipient)"
              >
                <span>{{ contract.returnAddress.recipient || '-' }}</span>
                <em :class="{ error: copyState.field === 'recipient' && copyState.error }">{{ copyFieldHint('recipient') }}</em>
              </button>
            </dd>
          </div>
          <div>
            <dt>联系电话</dt>
            <dd>
              <button
                type="button"
                class="copy-field"
                :disabled="!contract.returnAddress.phone"
                :aria-label="`复制联系电话 ${contract.returnAddress.phone || ''}`"
                @click="copyReturnField('phone', contract.returnAddress.phone)"
              >
                <span>{{ contract.returnAddress.phone || '-' }}</span>
                <em :class="{ error: copyState.field === 'phone' && copyState.error }">{{ copyFieldHint('phone') }}</em>
              </button>
            </dd>
          </div>
          <div class="full">
            <dt>收件地址</dt>
            <dd>
              <button
                type="button"
                class="copy-field"
                :disabled="!contract.returnAddress.address"
                :aria-label="`复制收件地址 ${contract.returnAddress.address || ''}`"
                @click="copyReturnField('address', contract.returnAddress.address)"
              >
                <span>{{ contract.returnAddress.address || '-' }}</span>
                <em :class="{ error: copyState.field === 'address' && copyState.error }">{{ copyFieldHint('address') }}</em>
              </button>
            </dd>
          </div>
        </dl>
        <p v-if="contract.instructions" class="courier-instructions">{{ contract.instructions }}</p>
        <p v-else class="courier-instructions">
          请在线预览、下载或查收合同后完成签字盖章，并将全部合同寄回上述平台收件地址。
        </p>

        <form @submit.prevent="submitReturnMail">
          <label>
            <span>寄回快递公司</span>
            <input
              v-model.trim="returnForm.company"
              name="returnCourierCompany"
              maxlength="50"
              required
              :disabled="!canEditReturnMail || submitting"
              placeholder="例如：顺丰速运"
            />
          </label>
          <label>
            <span>寄回快递单号</span>
            <input
              v-model.trim="returnForm.courierNo"
              name="returnCourierNo"
              maxlength="64"
              required
              :disabled="!canEditReturnMail || submitting"
              placeholder="请输入完整单号"
            />
          </label>
          <p v-if="returnMessage" class="return-message" :class="{ error: returnError }">{{ returnMessage }}</p>
          <button
            type="submit"
            class="primary"
            :disabled="!canEditReturnMail || submitting"
          >
            {{ submitting ? '正在保存...' : contract.returnCourierCompany ? '修改寄回信息' : '提交寄回信息' }}
          </button>
        </form>
      </div>

      <p v-if="contract.effectiveTime" class="effective-time">
        合同完成时间：{{ formatDateTime(contract.effectiveTime) }}
      </p>
    </template>

    <ContractPreviewDialog
      :open="previewVisible"
      :preview="previewData"
      :loading="previewLoading"
      :error="previewError"
      :downloading="Boolean(downloadingId)"
      :variant="variant"
      @close="previewVisible = false"
      @retry="openContractPreview(previewRecord)"
      @download="downloadContract(previewRecord)"
    />
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import ContractPreviewDialog from '@/components/ContractPreviewDialog.vue'
import {
  downloadWebsiteOrderContract,
  getWebsiteOrderContracts,
  previewWebsiteOrderContract,
  submitWebsiteContractReturnMail
} from '@/api/trade'
import { normalizeOrderContracts } from '@/utils/orderContracts'

const props = defineProps({
  orderId: {
    type: [String, Number],
    required: true
  },
  variant: {
    type: String,
    default: 'pc',
    validator: value => ['pc', 'mobile'].includes(value)
  }
})

const loading = ref(false)
const message = ref('')
const contract = reactive(normalizeOrderContracts({}))
const returnForm = reactive({ company: '', courierNo: '' })
const returnMessage = ref('')
const returnError = ref(false)
const submitting = ref(false)
const downloadingId = ref('')
const previewVisible = ref(false)
const previewLoading = ref(false)
const previewError = ref('')
const previewData = ref(null)
const previewRecord = ref(null)
const copyState = reactive({ field: '', error: false })
let copyStateTimer = 0

const canEditReturnMail = computed(() => !['EFFECTIVE', 'VOID'].includes(contract.status))
const statusClass = computed(() => ({
  effective: contract.status === 'EFFECTIVE',
  pending: ['WAIT_PAY', 'GENERATE_FAILED', 'WAIT_REGENERATE', 'WAIT_OUTBOUND'].includes(contract.status),
  outbound: ['OUTBOUND', 'RETURN_MAILED', 'PART_RECEIVED', 'RETURNED'].includes(contract.status),
  void: contract.status === 'VOID'
}))

/**
 * 查询订单合同详情并回填寄回快递表单。
 * @returns {Promise<void>} 查询结果
 */
async function loadContracts() {
  if (!props.orderId) return
  loading.value = true
  message.value = ''
  try {
    const result = normalizeOrderContracts(await getWebsiteOrderContracts(props.orderId))
    Object.assign(contract, result)
    returnForm.company = result.returnCourierCompany
    returnForm.courierNo = result.returnCourierNo
    returnMessage.value = ''
    returnError.value = false
  } catch (error) {
    message.value = error.message || '合同信息加载失败'
  } finally {
    loading.value = false
  }
}

/**
 * 鉴权下载单份合同并触发浏览器保存。
 * @param {object} record 合同记录
 * @returns {Promise<void>} 下载结果
 */
async function downloadContract(record) {
  if (!record?.id || !record.downloadEnabled || downloadingId.value) return
  downloadingId.value = record.id
  try {
    const { blob, filename } = await downloadWebsiteOrderContract(props.orderId, record.id)
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = record.fileName || filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  } catch (error) {
    returnMessage.value = error.message || '合同下载失败，请稍后重试'
    returnError.value = true
  } finally {
    downloadingId.value = ''
  }
}

/**
 * 打开单份已生成合同的HTML在线预览。
 * @param {object} record 合同记录
 * @returns {Promise<void>} 预览结果
 */
async function openContractPreview(record) {
  if (!record?.id || !record.downloadEnabled || previewLoading.value) return
  previewRecord.value = record
  previewVisible.value = true
  previewLoading.value = true
  previewError.value = ''
  try {
    previewData.value = await previewWebsiteOrderContract(props.orderId, record.id)
  } catch (error) {
    previewData.value = null
    previewError.value = error.message || '合同预览加载失败，请稍后重试。'
  } finally {
    previewLoading.value = false
  }
}

/**
 * 提交或修改用户寄回合同快递信息。
 * @returns {Promise<void>} 提交结果
 */
async function submitReturnMail() {
  if (!canEditReturnMail.value || submitting.value) return
  if (!returnForm.company) {
    returnMessage.value = '请填写寄回快递公司'
    returnError.value = true
    return
  }
  if (!returnForm.courierNo) {
    returnMessage.value = '请填写寄回快递单号'
    returnError.value = true
    return
  }
  submitting.value = true
  returnMessage.value = ''
  returnError.value = false
  try {
    const result = normalizeOrderContracts(await submitWebsiteContractReturnMail(
      props.orderId,
      returnForm.company,
      returnForm.courierNo
    ))
    Object.assign(contract, result)
    returnForm.company = result.returnCourierCompany
    returnForm.courierNo = result.returnCourierNo
    returnMessage.value = '寄回快递信息已保存'
  } catch (error) {
    returnMessage.value = error.message || '寄回快递信息保存失败'
    returnError.value = true
  } finally {
    submitting.value = false
  }
}

/**
 * 生成合同寄回信息字段的复制提示文案。
 * @param {string} field 字段标识
 * @returns {string} 复制提示文案
 */
function copyFieldHint(field) {
  if (copyState.field !== field) return '点击复制'
  return copyState.error ? '复制失败，请长按复制' : '已复制'
}

/**
 * 复制合同寄回信息并在按钮上展示短暂结果。
 * @param {string} field 字段标识
 * @param {string} value 待复制内容
 * @returns {Promise<void>} 复制结果
 */
async function copyReturnField(field, value) {
  const text = String(value || '').trim()
  if (!text) return
  try {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable')
      await navigator.clipboard.writeText(text)
    } catch {
      await copyTextWithFallback(text)
    }
    copyState.field = field
    copyState.error = false
  } catch {
    copyState.field = field
    copyState.error = true
    return
  }
  window.clearTimeout(copyStateTimer)
  copyStateTimer = window.setTimeout(() => {
    copyState.field = ''
    copyState.error = false
  }, 2000)
}

/**
 * 使用隐藏输入框兜底复制文本，兼容未开放剪贴板 API 的移动浏览器。
 * @param {string} text 待复制内容
 * @returns {Promise<void>} 复制结果
 */
async function copyTextWithFallback(text) {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.readOnly = true
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  try {
    if (!document.execCommand('copy')) throw new Error('copy failed')
  } finally {
    textarea.remove()
  }
}

/**
 * 格式化接口时间。
 * @param {string} value 时间字符串
 * @returns {string} 展示时间
 */
function formatDateTime(value) {
  return value ? String(value).replace('T', ' ').slice(0, 16) : '-'
}

watch(() => props.orderId, loadContracts, { immediate: true })

onBeforeUnmount(() => {
  window.clearTimeout(copyStateTimer)
})
</script>

<style scoped>
.order-contracts-panel {
  display: grid;
  gap: 14px;
  padding: 18px;
  border: 1px solid rgba(222, 246, 255, 0.16);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
}

.order-contracts-panel header,
.contract-info div,
.contract-record > div,
.contract-courier form {
  display: grid;
  gap: 6px;
}

.order-contracts-panel header {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
}

.order-contracts-panel header span:first-child,
.contract-list-head span:first-child,
.contract-courier dt,
.contract-courier h3 {
  color: rgba(226, 243, 255, 0.62);
}

.order-contracts-panel header > div > span {
  font-size: 12px;
  font-weight: 700;
}

.order-contracts-panel header strong {
  font-size: 17px;
}

.contract-status {
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid rgba(222, 246, 255, 0.22);
  color: #dff8ff;
  font-size: 11.5px;
  font-weight: 800;
  white-space: nowrap;
}

.contract-status.effective { border-color: rgba(98, 244, 214, .5); color: #8df5de; }
.contract-status.outbound { border-color: rgba(255, 207, 106, .5); color: #ffd5a3; }
.contract-status.void { border-color: rgba(255, 156, 156, .5); color: #ff9c9c; }

.contract-effect-tip,
.contract-empty,
.courier-instructions,
.effective-time,
.record-wrap {
  color: rgba(226, 243, 255, 0.68);
  font-size: 12px;
  line-height: 1.7;
}

.contract-info {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.contract-info span,
.contract-record span {
  color: rgba(226, 243, 255, 0.58);
  font-size: 11.5px;
}

.contract-info strong,
.contract-record strong {
  color: #f4fbff;
  font-size: 13px;
  line-height: 1.55;
  word-break: break-all;
}

.contract-list-head {
  display: flex;
  justify-content: space-between;
  color: rgba(226, 243, 255, 0.62);
  font-size: 12px;
  font-weight: 800;
}

.contract-record {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  border: 1px solid rgba(222, 246, 255, 0.12);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.025);
}

.contract-record em {
  width: fit-content;
  color: #ffd5a3;
  font-size: 11.5px;
  font-style: normal;
  font-weight: 800;
}

.contract-record em.record-received,
.contract-record em.record-generated { color: #8df5de; }
.contract-record em.record-rejected,
.contract-record em.record-void,
.contract-record em.record-generate_failed { color: #ff9c9c; }

.record-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.contract-record button,
.contract-state button,
.contract-courier form button {
  min-height: 34px;
  border: 0;
  border-radius: 9px;
  padding: 0 13px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.contract-record button,
.contract-state button {
  color: #04110f;
  background: linear-gradient(135deg, #8df5de, #46d6b6);
}

.contract-record button.preview {
  color: #8df5de;
  border: 1px solid rgba(98, 244, 214, .45);
  background: rgba(98, 244, 214, .08);
}

button:disabled {
  cursor: not-allowed;
  opacity: .55;
}

.contract-state,
.contract-state.error {
  display: grid;
  justify-items: center;
  gap: 10px;
  color: rgba(226, 243, 255, 0.68);
  font-size: 12px;
}

.contract-state.error p,
.return-message.error {
  color: #ff9c9c;
}

.contract-courier {
  display: grid;
  gap: 10px;
  padding: 13px;
  border: 1px dashed rgba(222, 246, 255, 0.18);
  border-radius: 13px;
}

.contract-courier h3 {
  margin: 0;
  font-size: 13px;
}

.contract-courier dl {
  display: grid;
  gap: 8px;
}

.contract-courier dl > div {
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr);
  gap: 10px;
}

.contract-courier dl > .full {
  grid-template-columns: 78px minmax(0, 1fr);
}

.contract-courier dt,
.contract-courier dd {
  font-size: 12px;
}

.contract-courier dd,
.return-message {
  margin: 0;
  color: #f4fbff;
  font-weight: 700;
  word-break: break-all;
}

.copy-field {
  display: grid;
  width: 100%;
  align-items: start;
  gap: 2px;
  border: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  background: transparent;
  appearance: none;
}

.copy-field span {
  color: inherit;
  font-weight: 700;
  word-break: break-all;
}

.copy-field em {
  color: rgba(141, 245, 222, .82);
  font-size: 11px;
  font-style: normal;
  font-weight: 750;
}

.copy-field em.error {
  color: #ff9c9c;
}

.copy-field:disabled {
  cursor: default;
}

.copy-field:disabled em {
  display: none;
}

.contract-courier form {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: end;
}

.contract-courier form label {
  display: grid;
  gap: 6px;
}

.contract-courier form span {
  color: rgba(226, 243, 255, 0.62);
  font-size: 11.5px;
  font-weight: 750;
}

.contract-courier input {
  width: 100%;
  min-height: 38px;
  border: 1px solid rgba(222, 246, 255, 0.18);
  border-radius: 9px;
  padding: 0 10px;
  color: #f4fbff;
  background: rgba(6, 15, 28, .68);
  font-size: 12px;
  box-sizing: border-box;
}

.contract-courier form button {
  color: #04110f;
  background: linear-gradient(135deg, #ffcf6a, #ffab4a);
}

.return-message {
  grid-column: 1 / -1;
  font-size: 12px;
  color: #8df5de;
}

.effective-time {
  margin: 0;
}

.order-contracts-panel.mobile {
  padding: 16px;
  border-color: var(--mobile-line);
  border-radius: 16px;
  background: var(--mobile-card);
}

.order-contracts-panel.mobile .copy-field {
  min-height: 44px;
}

.order-contracts-panel.mobile header strong {
  color: var(--mobile-ink);
  font-size: 15px;
}

.order-contracts-panel.mobile .contract-record,
.order-contracts-panel.mobile .contract-courier {
  border-color: var(--mobile-line);
  background: rgba(255,255,255,.035);
}

@media (max-width: 720px) {
  .contract-record,
  .contract-info,
  .contract-courier form {
    grid-template-columns: 1fr;
  }

  .record-actions {
    width: 100%;
  }

  .record-actions button {
    flex: 1 1 0;
  }
}
</style>
